import { useState, useEffect } from 'react'
import { Database } from '@/types/supabase'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Loader2, BookOpen, ExternalLink, Search, Filter, Tag, Clock } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { supabase } from '@/integrations/supabase/client'
import { useAuthStore } from '@/lib/store/auth'

// Define types from Supabase schema
type Book = Database['public']['Tables']['books']['Row']
type BookQueryParams = {
  category?: string;
  search?: string;
  orderBy?: 'title' | 'author' | 'reading_time' | 'order';
  orderDirection?: 'asc' | 'desc';
}

export function LibraryView() {
  const [books, setBooks] = useState<Book[]>([])
  const [allBooks, setAllBooks] = useState<Book[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<BookQueryParams['orderBy']>('order')
  
  const { user, caregiverId, role } = useAuthStore()

  // Fetch all books on component mount
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('books')
          .select('*')
          .order('order', { ascending: true })

        if (error) {
          throw error
        }

        setAllBooks(data || [])
        setBooks(data || [])
        
        // Extract unique categories
        const uniqueCategories = [...new Set(data?.map(book => book.category) || [])]
        setCategories(uniqueCategories)
      } catch (err) {
        setError('Error fetching books. Please try again later.')
        console.error('Error fetching books:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchBooks()
  }, [])

  // Filter books based on search query and category
  useEffect(() => {
    let filteredBooks = [...allBooks]
    
    // Apply category filter
    if (selectedCategory) {
      filteredBooks = filteredBooks.filter(book => 
        book.category.toLowerCase() === selectedCategory.toLowerCase()
      )
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filteredBooks = filteredBooks.filter(book => 
        book.title.toLowerCase().includes(query) || 
        book.author.toLowerCase().includes(query) || 
        book.description?.toLowerCase().includes(query) ||
        (book.tags && book.tags.some(tag => tag.toLowerCase().includes(query)))
      )
    }
    
    // Apply sorting
    filteredBooks.sort((a, b) => {
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title)
      } else if (sortBy === 'author') {
        return a.author.localeCompare(b.author)
      } else if (sortBy === 'reading_time') {
        return (a.reading_time || 0) - (b.reading_time || 0)
      } else {
        return (a.order || 0) - (b.order || 0)
      }
    })
    
    setBooks(filteredBooks)
  }, [allBooks, searchQuery, selectedCategory, sortBy])

  const openBookTemplate = (templateLink: string | null) => {
    if (!templateLink) return
    window.open(templateLink, '_blank')
  }

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(prevCategory => 
      prevCategory === category ? null : category
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-md bg-red-900/20 border border-red-800/30 text-red-400">
        {error}
      </div>
    )
  }

  return (
    <div className="w-full py-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
        <h1 className="text-2xl font-semibold text-blue-300">Sleep Resource Library</h1>
        
        <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search books..."
              className="pl-10 pr-4 py-2 bg-slate-800 border-slate-700 text-slate-200 w-full sm:w-64"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 whitespace-nowrap">Sort by:</span>
            <div className="flex rounded-md overflow-hidden border border-slate-700">
              <Button 
                variant="ghost" 
                size="sm"
                className={cn(
                  "rounded-none h-9 px-3 text-xs", 
                  sortBy === 'order' 
                    ? "bg-blue-900/40 text-blue-200" 
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                )}
                onClick={() => setSortBy('order')}
              >
                Featured
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className={cn(
                  "rounded-none h-9 px-3 text-xs", 
                  sortBy === 'title' 
                    ? "bg-blue-900/40 text-blue-200" 
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                )}
                onClick={() => setSortBy('title')}
              >
                Title
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className={cn(
                  "rounded-none h-9 px-3 text-xs", 
                  sortBy === 'reading_time' 
                    ? "bg-blue-900/40 text-blue-200" 
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                )}
                onClick={() => setSortBy('reading_time')}
              >
                Reading Time
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Categories */}
      {categories.length > 0 && (
        <div className="mb-6 overflow-x-auto pb-2">
          <div className="flex gap-2">
            {categories.map(category => (
              <Button
                key={category}
                variant="outline"
                size="sm"
                className={cn(
                  "whitespace-nowrap px-3 py-1", 
                  selectedCategory === category 
                    ? "bg-blue-900/40 text-blue-200 border-blue-700" 
                    : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
                )}
                onClick={() => handleCategoryClick(category)}
              >
                <Tag className="h-3.5 w-3.5 mr-1.5" />
                {category}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* Books grid */}
      {books.length === 0 ? (
        <div className="p-8 rounded-md bg-slate-800/50 border border-slate-700 text-slate-300 text-center">
          {searchQuery || selectedCategory ? (
            <>
              <div className="text-blue-300 mb-2 text-lg">No matching books found</div>
              <p>Try adjusting your search or filters</p>
            </>
          ) : (
            <>No books available in the library at the moment.</>
          )}
        </div>
      ) : (
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {books.map((book, index) => (
            <motion.div
              key={book.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card 
                className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 overflow-hidden flex flex-col h-full transition-all duration-200 hover:shadow-md hover:shadow-blue-900/20 hover:-translate-y-1"
              >
                <div className="aspect-[3/4] relative overflow-hidden bg-slate-900/50">
                  {book.cover_image_url ? (
                    <img 
                      src={book.cover_image_url} 
                      alt={book.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900/30 to-purple-900/30">
                      <BookOpen className="h-20 w-20 text-slate-600" />
                    </div>
                  )}
                  {book.category && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 rounded-full bg-blue-900/60 text-blue-200 text-xs backdrop-blur-sm">
                        {book.category}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="text-lg font-medium text-white mb-1 line-clamp-1">{book.title}</h3>
                  <p className="text-sm text-blue-300 mb-2">by {book.author}</p>
                  
                  {book.description && (
                    <p className="text-sm text-slate-300 mb-4 line-clamp-3 flex-1">
                      {book.description}
                    </p>
                  )}
                  
                  <div className="mt-auto pt-2">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {book.reading_time && (
                        <div className="flex items-center text-xs text-slate-400">
                          <Clock className="h-3.5 w-3.5 mr-1 text-blue-400" />
                          <span>{book.reading_time} min read</span>
                        </div>
                      )}
                      
                      {book.tags && book.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {book.tags.slice(0, 3).map(tag => (
                            <span 
                              key={tag} 
                              className="px-1.5 py-0.5 rounded-sm bg-slate-800 border border-slate-700 text-slate-400 text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {book.tags.length > 3 && (
                            <span className="text-xs text-slate-500">+{book.tags.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => openBookTemplate(book.templatelink)}
                      disabled={!book.templatelink}
                      className={cn(
                        "w-full group",
                        book.templatelink 
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500" 
                          : "bg-slate-700 text-slate-300 cursor-not-allowed"
                      )}
                    >
                      <ExternalLink className="h-4 w-4 mr-2 transition-transform group-hover:translate-x-0.5" />
                      Open Resource
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  )
}
